import { IPricingChartModularBlock } from "@/.generated";

export type ParsedSegment = {
  startHour: number;
  endHour: number;
  label: string;
  fill: string;
  numericValue: number;
  topMargin: number;
};

export const TOTAL_HOURS = 24;
export const FALLBACK_FILL = '#84AB3B';
export const MAX_BAR_AREA_PX = 80;

// Parses a CMS top_margin value (e.g. "20px") into a number; returns null if missing/invalid
export const parseTopMargin = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const match = value.match(/^(\d+)px$/);
  return match ? parseInt(match[1], 10) : null;
};

export const parseTimeSlot = (time: string | null | undefined): number | null => {
  if (!time) return null;
  const match = time.match(/^(\d+)-(am|pm)$/);
  if (!match) return null;
  const hour = parseInt(match[1], 10);
  const period = match[2];
  if (period === 'am') {
    if (hour === 0) return 0;
    if (hour === 12) return 24;
    return hour;
  }
  if (hour === 12) return 12;
  return hour + 12;
};

export const parseNumericValue = (value: string | undefined): number => {
  if (!value) return 0;
  const match = value.match(/[\d.]+/);
  return match ? parseFloat(match[0]) : 0;
};

export const parseSegments = (items: IPricingChartModularBlock['items']): ParsedSegment[] => {
  if (!items?.length) return [];
  return items.flatMap<ParsedSegment>((item) => {
    // Skip items without a CMS-provided top_margin so no bar is drawn
    const topMargin = parseTopMargin(item?.top_margin) || 0;

    const startHour = parseTimeSlot(item?.start_time?.time);
    let endHour = parseTimeSlot(item?.end_time?.time);
    if (startHour === null || endHour === null) return [];
    if (endHour <= startHour) endHour += TOTAL_HOURS;
    if (endHour - startHour <= 0 || endHour > TOTAL_HOURS) return [];
    return [
      {
        startHour,
        endHour,
        label: item?.value ?? '',
        fill: item?.color?.color_list ?? FALLBACK_FILL,
        numericValue: parseNumericValue(item?.value),
        topMargin,
      },
    ];
  });
};
